import { useState } from 'react';
import clsx from 'clsx';

interface JamesClarifyingQuestionProps {
  question: string;
  options: Array<{ label: string; description?: string }>;
  onSelect: (text: string) => void;
}

export function JamesClarifyingQuestion({ question, options, onSelect }: JamesClarifyingQuestionProps) {
  const [customText, setCustomText] = useState('');

  const handleCustomSubmit = () => {
    const trimmed = customText.trim();
    if (trimmed) {
      onSelect(trimmed);
      setCustomText('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleCustomSubmit();
    }
  };

  return (
    <div className="px-4 pb-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-4xl rounded-2xl border border-border-light bg-surface-primary p-5">
        <div className="flex items-center gap-2">
          <span className="pill text-body-xs uppercase tracking-wider">Clarify</span>
          <span className="text-body-xs text-text-tertiary">Choose an option or answer in your own words</span>
        </div>
        <p className="mb-4 mt-3 text-body-sm font-medium text-text-primary">{question}</p>

        <div className="mb-4 space-y-2">
          {options.map((option, index) => (
            <button
              key={index}
              onClick={() => onSelect(option.label)}
              className="w-full rounded-xl border border-border-light bg-surface-secondary px-4 py-3 text-left text-body-sm text-text-primary transition-colors hover:border-border hover:bg-surface-primary"
            >
              <span className="mr-2 font-medium text-text-secondary">{index + 1}.</span>
              {option.label}
              {option.description && (
                <span className="mt-1 block pl-5 text-body-xs text-text-tertiary">
                  {option.description}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <input
            type="text"
            value={customText}
            onChange={(e) => setCustomText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Something else..."
            className={clsx(
              'flex-1 rounded-xl border border-border-light bg-surface-primary px-4 py-2.5 text-body-sm text-text-primary placeholder-text-muted',
              'focus:border-text-primary focus:outline-none focus:ring-1 focus:ring-text-primary',
              'transition-colors'
            )}
          />
          <button
            onClick={handleCustomSubmit}
            disabled={!customText.trim()}
            className={clsx(
              'rounded-xl px-4 py-2.5 text-body-sm font-medium transition-colors',
              'bg-surface-inverse text-text-inverse',
              'hover:bg-text-secondary',
              'disabled:cursor-not-allowed disabled:opacity-40'
            )}
          >
            Send
          </button>
        </div>

        <button
          onClick={() => onSelect('Skip this question and proceed with your best judgment')}
          className="mt-3 text-body-xs text-text-tertiary transition-colors hover:text-text-secondary"
        >
          Skip and proceed with best judgment
        </button>
      </div>
    </div>
  );
}
